import React, { useState, useEffect } from 'react';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import arrowRight from '../../images/arrowRight.svg';
import arrowLeft from '../../images/arrowLeft.svg';
import externalLink from '../../images/externalLink.svg';
import whereCare from '../../images/whereCare.svg';
import typeCare from '../../images/typeCare.svg';
import healthCon from '../../images/healthCon.svg';
import starCircle from '../../images/starCircle.svg';
import config from '../../config/appConfig';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import './styles.css';
import { apicFetch } from '../../services/apiTokenService';
import { isLocalDevMode } from '../../utils/devMode';
import { dummyPaBrowseByResponse } from '../../data/dummyPaData';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useTranslation } from '../../contexts/TranslationContext';
import ApiErrorBanner from '../errorMessages/ApiErrorBanner';
import { ERROR_TYPES, getErrorMessage } from '../errorMessages/errorMessages.constants';
import { classifyApiError } from '../../utils/apiErrorClassifier';

const BrowseBy = ({ onSearchClick = () => {} }) => {
    const { environmentId, isEmergencyCareBrowseByEnabled } = usePlanFeatures();
    // Whether to show the "Emergency and Urgent Care" entry (button + its 911 informational
    // copy) in the Browse by Care side sheet. Driven by the top-level
    // featureFlags.emergencyCareBrowseBy plan flag — fail-open, only an explicit 'N' hides it
    // (see isEmergencyCareBrowseByEnabled in PlanFeaturesContext). First requested for NY.
    const isEmergencyEntryHidden = !isEmergencyCareBrowseByEnabled();
    // Akorbi (crestTranslator) only scans/translates whatever's in the DOM at the moment
    // start() is called (see TranslationContext's own comments). This component's category
    // buttons/subcategory panel content are BOTH loaded/rendered asynchronously — well after
    // that initial start() call or any explicit language switch — via fetchBrowseByCategories
    // below (browseByData) and via the side panel opening/drilling into subcategories
    // (panelOpen/subcategoryPath). Without re-running translation whenever any of that new
    // content lands in the DOM, a previously-selected non-English language never gets applied
    // to it — matching "content loaded dynamically... not getting translated as it's updated
    // after translation".
    const { selectedLanguage, retranslateContent } = useTranslation();
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [browseByData, setBrowseByData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subcategoryPath, setSubcategoryPath] = useState([]);
    const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
    const [pendingExternalUrl, setPendingExternalUrl] = useState(null);
    const panelRef = React.useRef(null);
    const triggerRef = React.useRef(null);

    // Lock page scroll while the category panel is open (mobile only, matching this
    // panel's original mobile-only overflow lock), without jumping to the top and
    // correctly restoring the exact scroll position on close.
    useBodyScrollLock(panelOpen && typeof window !== 'undefined' && window.innerWidth <= 767);

    useEffect(() => {
        const fetchBrowseByCategories = async () => {
            console.log('🏷️ Fetching Browse By Categories:', {
                environmentId,
                timestamp: new Date().toISOString()
            });
            
            setLoading(true);
            setError(null);

            // On localhost, use the hardcoded PA dummy browse-by/specialties response
            // instead of hitting the real API through the /api-proxy dev proxy.
            if (isLocalDevMode()) {
                console.log('🧪 Using dummy PA browse-by categories (local dev mode)');
                const browseByItem = dummyPaBrowseByResponse.browseBy?.[0];
                if (browseByItem) {
                    setBrowseByData(browseByItem.browseByCategories);
                    setError(null);
                } else {
                    setError('No browse by data found in dummy response');
                    setBrowseByData(null);
                }
                setLoading(false);
                return;
            }

            let apiUrl;
            if (import.meta.env.DEV && window.location.hostname === 'localhost') {
                // Use proxy path for local development
                apiUrl = `/api-proxy/hcb/v3/planspecialty/plans/${environmentId}/specialties`;
            } else {
                // Use environment-specific API base URL
                apiUrl = `${config.APIC_BASE_URL_API}/hcb/v3/planspecialty/plans/${environmentId}/specialties`;
            }
            
            console.log('🏷️ Browse By Categories API URL:', apiUrl);
            
            try {
                const response = await apicFetch(apiUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                const data = await response.json();
                
                console.log('✅ Browse By Categories Response:', {
                    environmentId,
                    browseByCount: data.browseBy?.length || 0,
                    firstItem: data.browseBy?.[0] || null
                });
                
                const browseByItem = data.browseBy?.[0];
                if (browseByItem) {
                    setBrowseByData(browseByItem.browseByCategories);
                } else {
                    throw new Error('No browse by data found in response');
                }
            } catch (err) {
                console.error('❌ Browse By Categories Error:', err);
                // Classify + surface the shared, plan-aware APIC/MCS backend error
                // message instead of the raw thrown error text. See
                // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md.
                const classification = classifyApiError({ error: err });
                console.error('❌ Browse By Categories Classified Error:', classification);
                setError(getErrorMessage(ERROR_TYPES.API));
                setBrowseByData(null);
            } finally {
                setLoading(false);
            }
        };
        if (environmentId || isLocalDevMode()) fetchBrowseByCategories();
    }, [environmentId]);

    // Re-trigger Akorbi translation once the (async-loaded) category buttons finish
    // rendering — see the comment above useTranslation() for why this is needed. Waits
    // until loading is false so it runs against the actual final DOM, not the disabled
    // placeholder buttons shown while the fetch is still in flight.
    useEffect(() => {
        if (loading) return;
        retranslateContent();
    }, [browseByData, loading, selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    // The side panel's own content (category list, subcategory drill-downs, quick
    // links/more-results sections) is swapped in/out independently of browseByData —
    // opening the panel (panelOpen) or drilling into/back out of a subcategory
    // (subcategoryPath) both render brand-new DOM nodes that were never scanned by the
    // translator. Re-run translation whenever either changes so the panel's content
    // gets translated too, not just the top-level "Browse by" buttons above.
    useEffect(() => {
        if (!panelOpen) return;
        retranslateContent();
    }, [panelOpen, subcategoryPath, selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    const getDisplayCategories = () => {
        if (!browseByData) return ['Types of care', 'Where to get care', 'Health conditions'];
        const categories = [];
        if (browseByData.typesOfCare) categories.push('Types of care');
        if (browseByData.whereToGetCare) categories.push('Where to get care');
        if (browseByData.healthConditions) categories.push('Health conditions');
        return categories;
    };

    const getCategoryImage = (category) => {
        const categoryLower = category.toLowerCase();
        if (categoryLower.includes('where')) return whereCare;
        if (categoryLower.includes('type')) return typeCare;
        if (categoryLower.includes('health')) return healthCon;
        return null;
    };

    const getSubcategories = (category) => {
        if (!browseByData) return [];
        const keyMap = {
            'healthconditions': 'healthConditions',
            'typesofcare': 'typesOfCare',
            'wheretogetcare': 'whereToGetCare'
        };
        const categoryKey = category.toLowerCase().replace(/\s+/g, '');
        const apiKey = keyMap[categoryKey];
        return browseByData[apiKey] || [];
    };

    const getCurrentSubcategories = () => {
        if (!selectedCategory) return [];
        let subcats = getSubcategories(selectedCategory);
        for (const key of subcategoryPath) {
            if (Array.isArray(subcats)) {
                subcats = subcats[key];
            } else if (typeof subcats === 'object' && subcats !== null) {
                subcats = subcats[key];
            } else {
                return [];
            }
        }
        if (subcats && typeof subcats === 'object' && subcats.subCategories) {
            return subcats.subCategories;
        }
        return subcats;
    };

    // `lookingForText` is normally a plain string (see e2e/fixtures/mockData.js's
    // mockBrowseByResponse for the established precedent, and .looking-for-label's
    // fixed single-line `height: 26px` in styles.css, which only budgets room for one
    // line of text). Some CMS-authored categories instead supply it as a 2-element
    // array — a generic fallback label at index 0 ("I'm looking for:", matching this
    // file's own hardcoded default a bit further down) and a more specific,
    // category-tailored heading at index 1 (e.g. "Lab work quick links"). Prefer that
    // more specific trailing string when an array is given; pass plain strings through
    // unchanged. Without this, React renders array children by concatenating them with
    // no separator/whitespace at all, producing a squished, unreadable
    // "I'm looking for:Lab work quick links" in the single-line label box.
    const getLookingForLabel = (value) => {
        if (Array.isArray(value)) return value[value.length - 1] || '';
        return value || '';
    };

    const getCategoryLabel = (item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
            if (item.subCategory) return item.subCategory;
            if (item.lookingForText) return getLookingForLabel(item.lookingForText);
            return item.name || item.label || Object.keys(item)[0] || 'Subcategory';
        }
        return 'Subcategory';
    };

    const getCategoryDescription = (item) => {
        if (item && typeof item === 'object' && item.description) return item.description;
        return null;
    };

    // Guards against "widowed" arrows: with a plain `<span>{label}</span><img .../>`
    // pair, the browser wraps the label's text and the trailing arrow icon completely
    // independently — since category labels are variable-length strings (fed by the
    // API/CMS), it was common for the label's last line of wrapped text to run right up
    // to the button's edge, pushing the arrow icon alone onto its own line with no
    // accompanying text (reported defect). Fix: split the label at its last space and
    // wrap that trailing word together with the arrow icon in a single
    // `white-space: nowrap` unit (see .category-item-label-tail in styles.css), so the
    // browser can never break the line between that last word and the arrow — if a
    // wrap is needed, the last word + arrow always move to the next line TOGETHER.
    const renderLabelWithArrow = (label, arrowImgProps) => {
        const text = String(label ?? '');
        const lastSpaceIndex = text.lastIndexOf(' ');
        if (lastSpaceIndex === -1) {
            return (
                <span className="category-item-label-tail">
                    {text}
                    <img {...arrowImgProps} />
                </span>
            );
        }
        const head = text.slice(0, lastSpaceIndex);
        const tail = text.slice(lastSpaceIndex + 1);
        return (
            <>
                {/* Fix: `head` must be wrapped in its own <span> — .category-item span
                    is what actually applies the purple link color/font styling (see
                    styles.css), and a bare text node sitting outside any <span> doesn't
                    match that selector at all. Without this, only the last word (inside
                    .category-item-label-tail, itself a <span>) got the purple styling,
                    while every word before it rendered in the button's inherited default
                    (black) text color — the reported "black text, then purple text, then
                    arrow" appearance. */}
                <span>{head}</span>{' '}
                <span className="category-item-label-tail">
                    {tail}
                    <img {...arrowImgProps} />
                </span>
            </>
        );
    };

    const hasExternalLink = (item) => {
        if (item && typeof item === 'object') {
            return !!(item.url || item.link || item.externalLink || item.href);
        }
        return false;
    };

    const handleButtonClick = (category, e) => {
        triggerRef.current = e?.currentTarget ?? document.activeElement;
        setSelectedCategory(category);
        setPanelOpen(true);
    };

    const handleClosePanel = () => {
        setPanelOpen(false);
        setSelectedCategory('');
        setSubcategoryPath([]);
        triggerRef.current?.focus({ preventScroll: true });
    };

    useEffect(() => {
        if (panelOpen && panelRef.current) {
            const focusableElements = panelRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];

            // Move focus into the panel when it opens
            firstElement?.focus({ preventScroll: true });

            // Query focusable elements FRESH on every Tab press rather than capturing a
            // single stale NodeList once (on mount / whenever `panelOpen` toggles). This
            // panel's content changes WITHOUT `panelOpen` ever changing — e.g. drilling
            // into a subcategory (setSubcategoryPath, from pressing Enter on "Emergency
            // and Urgent Care" or any other drill-down item) swaps in a new "Back to
            // {previousTitle}" button at the top and removes the "X" close button (see
            // the `subcategoryPath.length === 0` condition on that button below) — so the
            // captured `firstElement` reference became a detached/unmounted DOM node the
            // instant that happened. Calling `.focus()` on a detached node is a no-op, so
            // Tab from "Back to Find care" (the trap's still-valid `lastElement`, always
            // rendered) silently did nothing — matching "focus doesn't automatically go to
            // Back to Where to get care" — and because focus never actually moved off
            // "Back to Find care", the Shift+Tab branch's `document.activeElement ===
            // firstElement` check never matched either, so the trap did nothing on
            // Shift+Tab and normal browser tab order took over, walking focus backward out
            // of the (visually-overlaid-but-still-in-DOM) panel entirely and onto the
            // "Find care" page behind it — matching both remaining reported symptoms.
            // Recomputing on every keydown keeps the trap correct across any such
            // in-place content change, not just the initial open.
            const getFocusable = () => Array.from(
                panelRef.current.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            );

            const handleTabKey = (e) => {
                if (e.key !== 'Tab') return;
                const currentFocusable = getFocusable();
                if (currentFocusable.length === 0) return;
                const currentFirst = currentFocusable[0];
                const currentLast = currentFocusable[currentFocusable.length - 1];

                if (e.shiftKey && document.activeElement === currentFirst) {
                    e.preventDefault();
                    currentLast?.focus();
                } else if (!e.shiftKey && document.activeElement === currentLast) {
                    e.preventDefault();
                    currentFirst?.focus();
                }
            };
            panelRef.current.addEventListener('keydown', handleTabKey);
            return () => { panelRef.current?.removeEventListener('keydown', handleTabKey); };
        }
    }, [panelOpen]);

    // Explicitly move focus to the panel's new first focusable element (the "Back to
    // {previousTitle}" header button when drilled into a subcategory, or the "X" close
    // button at the top level) every time subcategoryPath changes - i.e. every drill-down
    // (clicking e.g. "Emergency and Urgent Care") or drill-up (clicking "Back to...").
    // Without this, the item that was just clicked (inside .panel-content) is removed/
    // replaced as part of rendering the new level, and the browser's default "focus was on
    // a now-removed node" recovery kicks in - which typically moves focus to the next
    // focusable sibling *in DOM order* after the removed one, not to any element WE intend.
    // Since .panel-content is followed in the DOM by .panel-footer's "Back to Find care"
    // button, that's exactly where focus silently landed instead - reported as "the focus
    // stays in the side bar, but it focuses on the 'Back to Find care' button first"
    // (still trapped, just on the wrong/confusing element for a mid-drill-down navigation).
    // Skip the very first render (handled by the panelOpen effect above already) by only
    // running once panelOpen is true AND this isn't the initial open.
    const isInitialPanelOpen = React.useRef(true);
    useEffect(() => {
        if (!panelOpen) {
            isInitialPanelOpen.current = true;
            return;
        }
        if (isInitialPanelOpen.current) {
            isInitialPanelOpen.current = false;
            return;
        }
        if (panelRef.current) {
            const focusableElements = panelRef.current.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            focusableElements[0]?.focus({ preventScroll: true });
        }
    }, [subcategoryPath, panelOpen]);

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('panel-overlay')) handleClosePanel();
    };

    const handleCategorySearch = (subcategory, isSubcategory = false, parentCategory = null) => {
        handleClosePanel();
        // If this is a subcategory drill-down, pass both the parent and subcategory
        if (isSubcategory && (parentCategory || selectedCategory)) {
            // Use the provided parentCategory if available, otherwise fall back to selectedCategory
            const actualParent = parentCategory || selectedCategory;
            // Pass an object with both values
            onSearchClick({ superCategory: actualParent, subCategory: subcategory });
        } else {
            // Regular category search - just pass the category name
            onSearchClick(subcategory);
        }
    };

    const handleSubcategoryClick = (subcategory, index) => {
        if (typeof subcategory === 'object' && subcategory.lookingForText && Array.isArray(subcategory.subCategories)) {
            setSubcategoryPath([...subcategoryPath, index]);
        } else if (hasExternalLink(subcategory)) {
            // If it's an external link, show modal before opening
            const url = subcategory.url || subcategory.link || subcategory.externalLink || subcategory.href;
            if (url) {
                setPendingExternalUrl(url);
                setExternalLinkModalOpen(true);
            }
        } else {
            // Check if we're in a subcategory drill-down (subcategoryPath has items)
            const isSubcategoryDrilldown = subcategoryPath.length > 0;
            
            // If we're drilling down, get the actual parent category name
            let parentCategoryName = selectedCategory;
            if (isSubcategoryDrilldown) {
                // Navigate to find the parent category label
                let subcats = getSubcategories(selectedCategory);
                for (let i = 0; i < subcategoryPath.length; i++) {
                    const key = subcategoryPath[i];
                    if (Array.isArray(subcats) && key > 0) {
                        // The label is typically the string item right before the nested object
                        const labelItem = subcats[key - 1];
                        if (typeof labelItem === 'string') {
                            parentCategoryName = labelItem;
                        }
                    }
                    // Navigate deeper
                    if (Array.isArray(subcats)) {
                        subcats = subcats[key];
                    } else if (typeof subcats === 'object' && subcats !== null) {
                        subcats = subcats[key];
                    }
                }
            }
            
            handleCategorySearch(getCategoryLabel(subcategory), isSubcategoryDrilldown, parentCategoryName);
        }
    };

    // TODO: destination URL for the Labs panel's "More Results" link ("Labwork: more
    // results") hasn't been provided yet — UI/layout only for now, per request. Wire
    // this up (likely via the same external-link-confirmation modal as the Quick Links
    // items above, using setPendingExternalUrl/setExternalLinkModalOpen) once it's
    // available.
    const handleMoreResultsLinkClick = () => {
        console.log('🔗 "Labwork: more results" clicked — destination URL not yet provided');
    };

    const handleExternalLinkConfirm = () => {
        if (pendingExternalUrl) {
            window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
        }
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    };

    const handleExternalLinkCancel = () => {
        setExternalLinkModalOpen(false);
        setPendingExternalUrl(null);
    };

    const handleEmergencyClick = () => {
        // Safety check: ensure browseByData is loaded
        if (!browseByData || loading) {
            // If data isn't ready, fallback to search
            handleCategorySearch('Emergency and Urgent Care');
            return;
        }

        // Matches either "emergency" or "emergencies" (the latter does NOT contain the
        // former as a substring), case-insensitively.
        const isEmergencyText = (text) => {
            const lower = String(text || '').toLowerCase();
            return lower.includes('emergency') || lower.includes('emergencies');
        };

        // Try to find "Emergency and urgent care" or similar in the current category's subcategories
        const subcats = getCurrentSubcategories();
        
        if (subcats && subcats.length > 0) {
            // Look for an item that matches "Emergency and urgent care" or has it as a subcategory
            const emergencyIndex = subcats.findIndex((item, idx) => {
                // Check if it's a string match
                if (typeof item === 'string' && isEmergencyText(item)) {
                    return true;
                }
                // Check if it's an object with the label
                if (item && typeof item === 'object') {
                    const label = getCategoryLabel(item);
                    if (label && isEmergencyText(label)) {
                        return true;
                    }
                }
                // Check if the next item is a subcategory object for this string
                if (typeof item === 'string' && idx < subcats.length - 1) {
                    const next = subcats[idx + 1];
                    if (next && typeof next === 'object' && next.lookingForText && Array.isArray(next.subCategories)) {
                        if (isEmergencyText(item)) {
                            return true;
                        }
                    }
                }
                return false;
            });

            if (emergencyIndex !== -1) {
                const emergencyItem = subcats[emergencyIndex];
                
                // Check if this is a string followed by a subcategory object
                if (typeof emergencyItem === 'string' && emergencyIndex < subcats.length - 1) {
                    const next = subcats[emergencyIndex + 1];
                    if (next && typeof next === 'object' && next.lookingForText && Array.isArray(next.subCategories)) {
                        // Navigate to the subcategory panel
                        handleSubcategoryClick(next, emergencyIndex + 1);
                        return;
                    }
                }
                
                // Otherwise, handle it as a regular subcategory click
                handleSubcategoryClick(emergencyItem, emergencyIndex);
                return;
            }
        }
        
        // Fallback: just search for "Emergency and Urgent Care"
        handleCategorySearch('Emergency and Urgent Care');
    };

    const handleBack = () => {
        if (subcategoryPath.length > 0) {
            setSubcategoryPath(subcategoryPath.slice(0, -1));
        } else {
            handleClosePanel();
        }
    };

    let subcategories = selectedCategory ? getCurrentSubcategories() : [];
    let overlayTitle = selectedCategory ? "I'm looking for:" : '';
    let panelHeaderTitle = selectedCategory || ''; // Start with category name, not "I'm looking for:"
    let previousTitle = selectedCategory || '';
    let currentSubcategoryTitle = null; // Track the actual subcategory title
    // Set alongside overlayTitle/subcategories below, only for a drill-down level whose
    // raw (pre-getLookingForLabel) lookingForText is a 3-element array — e.g.
    // ["I'm looking for:", "More Results", "Quick links"] for the Labs panel. Unlike
    // every other drill-down (plain 1-2 element lookingForText, one flat list of
    // clickable subCategory items), this shape means the panel body is split into TWO
    // separate sections, each with its own header + subtext: "Quick links" (external
    // provider links) first, then "More Results" (a single additional resource link)
    // 30px below it. See the dedicated render branch below (search
    // isQuickLinksSection) instead of the generic subcategories.map() loop.
    let isQuickLinksSection = false;
    let isLabsPanel = false; // Track if we're in the Labs panel for special handling
    let isDentalPanel = false; // Track if we're in the Dental panel for special handling
    let quickLinksHeader = '';
    let moreResultsHeader = '';
    let quickLinksItems = [];
    // The single link-less item (e.g. "Find more lab providers") backing the "More
    // Results" section's button. Lifted to this outer scope (not just read locally
    // for its description) so the render below can use its REAL subCategory label/
    // index for an internal subcategory search via handleSubcategoryClick — same as
    // every other non-external-link subcategory item — instead of a hardcoded
    // "Labwork: more results" placeholder button that went nowhere.
    let moreResultsItem = null;
    let quickLinksSubtext = null;
    let moreResultsSubtext = null;
    
    if (selectedCategory && subcategoryPath.length > 0) {
        let subcats = getSubcategories(selectedCategory);
        for (let i = 0; i < subcategoryPath.length; i++) {
            const key = subcategoryPath[i];
            if (i === subcategoryPath.length - 2) {
                // This is the previous level
                if (Array.isArray(subcats)) {
                    const prevItem = subcats[key];
                    if (prevItem && typeof prevItem === 'object' && prevItem.lookingForText) {
                        previousTitle = getLookingForLabel(prevItem.lookingForText);
                    }
                } else if (typeof subcats === 'object' && subcats !== null) {
                    previousTitle = getLookingForLabel(subcats.lookingForText) || previousTitle;
                }
            }
            
            if (Array.isArray(subcats)) {
                subcats = subcats[key];
            } else if (typeof subcats === 'object' && subcats !== null) {
                subcats = subcats[key];
            } else {
                subcats = null;
                break;
            }
        }
        if (subcats && typeof subcats === 'object' && subcats.lookingForText && Array.isArray(subcats.subCategories)) {
            subcategories = subcats.subCategories;
            // Detect the "Quick links" + "More Results" two-section layout structurally —
            // by whether subCategories contains a mix of externalLink items (quick links,
            // e.g. LabCorp/Quest) and non-externalLink items (the "more results" entry,
            // e.g. "Find more lab providers") — rather than requiring `lookingForText` to
            // be a 3+-element array. Previously this only checked
            // `lookingForText.length > 2`, so a Labs response with only ONE quick-link item
            // (e.g. just Quest Diagnostics, no LabCorp) — which naturally shortens
            // `lookingForText` to just 2 elements (["I'm looking for:", "More places to get
            // lab work"]) — fell through to the generic single-list rendering path instead
            // of keeping the same two-section layout. The section split should depend on
            // the DATA (how many/which links exist), not on how many header strings happen
            // to accompany it.
            isQuickLinksSection = subcategories.some(hasExternalLink) && subcategories.some(item => !hasExternalLink(item));
            if (isQuickLinksSection) {
                // Index 0 is still the standard "I'm looking for:" overlay label; the
                // other two (when present) are this panel's two section headers,
                // "Quick links" (last) shown first/above "More Results" (2nd-to-last) per
                // the requested layout. When lookingForText is shorter (e.g. only 2
                // elements, as happens when there's just a single quick-link item), fall
                // back to the standard "Quick links"/"More results" header labels instead
                // of indexing past the array.
                const lft = subcats.lookingForText;
                overlayTitle = Array.isArray(lft) ? lft[0] : lft;
                if (Array.isArray(lft) && lft.length > 2) {
                    moreResultsHeader = lft[lft.length - 2];
                    quickLinksHeader = lft[lft.length - 1];
                } else {
                    moreResultsHeader = 'More results';
                    quickLinksHeader = 'Quick links';
                }

                // Items WITH an external link (LabCorp, Quest Diagnostics) belong in the
                // "Quick links" section, in the source data's natural order (LabCorp,
                // then Quest Diagnostics) — this is also alphabetical, which is the
                // requested display order, so no reordering is needed here.
                quickLinksItems = subcategories.filter(hasExternalLink);
                // The one link-less item ("Find more lab providers") is this drill-down's
                // "More Results" entry instead of a plain header — its own `description`
                // becomes that section's subtext, while whichever quick-link item carries
                // a description (LabCorp's, in the sample data) supplies the quick-links
                // section's shared subtext line.
                moreResultsItem = subcategories.find(item => !hasExternalLink(item));
                moreResultsSubtext = moreResultsItem ? getCategoryDescription(moreResultsItem) : null;
                quickLinksSubtext = quickLinksItems.map(getCategoryDescription).find(Boolean) || null;
            } else {
                overlayTitle = getLookingForLabel(subcats.lookingForText);
            }
            // For panel header when in subcategory, find the parent string that precedes this object
            // Look back in the original array to find the label
            const parentCats = getSubcategories(selectedCategory);
            const lastPathIndex = subcategoryPath[subcategoryPath.length - 1];
            if (Array.isArray(parentCats) && lastPathIndex > 0) {
                const labelItem = parentCats[lastPathIndex - 1];
                if (typeof labelItem === 'string') {
                    currentSubcategoryTitle = labelItem;
                }
            }
            panelHeaderTitle = currentSubcategoryTitle || selectedCategory;
            isLabsPanel = (panelHeaderTitle || '').toLowerCase().includes('labs');
            isDentalPanel = (panelHeaderTitle || '').toLowerCase().includes('dental');
        }
    }


    return (
        <>
            <div id="browseByDiv" className="mb30">
                <h3 id="browseByTitle">Browse by</h3>
                <div className="browseByDescription">
                  These are just some of the providers in our network. You can browse&nbsp;by&nbsp;topic below. Or&nbsp;just type what&nbsp;you’re&nbsp;looking for&nbsp;into&nbsp;the Provider info field above.
                </div>
                {/* APIC/MCS backend error state — see reports/SPECIFIC_ERROR_MESSAGES_PLAN.md */}
                {!loading && error && (
                    <ApiErrorBanner type={ERROR_TYPES.API} />
                )}
                <div 
                    className="browseByButtonRow"
                    role="group"
                    aria-labelledby="browseByTitle"
                >
                    {getDisplayCategories().map((category, index) => {
                        const categoryImage = getCategoryImage(category);
                        return (
                            <button 
                                key={`category-${index}-${category}`}
                                className="browseByButton"
                                onClick={(e) => handleButtonClick(category, e)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleButtonClick(category, e);
                                    }
                                }}
                                aria-label={`Browse by ${category}`}
                                disabled={loading}
                                type="button"
                            >
                                {categoryImage && <img src={categoryImage} alt="" className="browseByIcon" />}
                                {category}
                            </button>
                        );
                    })}
                </div>
                {/* Loading state is still announced to screen readers (visually hidden)
                    even though the visible "Loading browse categories..." text line was
                    removed per design — buttons are already disabled (see disabled={loading}
                    above) as the visible-enough loading indicator. */}
                {loading && (
                    <span className="sr-only" role="status" aria-live="polite">
                        Loading browse categories...
                    </span>
                )}
            </div>
            {panelOpen && (
                <div 
                    className="panel-overlay" 
                    onClick={handleOverlayClick}
                    onKeyDown={(e) => { if (e.key === 'Escape') handleClosePanel(); }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="panel-title"
                    aria-describedby="panel-content"
                >
                    <div className="side-panel" ref={panelRef}>
                        <div className="panel-header">
                            {subcategoryPath.length > 0 && (
                                <div className="back-link-header-container">
                                    <button className="back-link" onClick={handleBack}>
                                        <img src={arrowLeft} alt="" className="back-arrow" aria-hidden="true" />
                                        <span className="back-text">Back to {previousTitle}</span>
                                    </button>
                                </div>
                            )}
                            <div className="panel-title-row">
                                <h3 id="panel-title" className="panel-title">
                                    {panelHeaderTitle}
                                </h3>
                                {/* Sub-category drill-down sheets (subcategoryPath.length > 0) shouldn't show
                                    an "X" close button in the title bar — they already have the "Back to
                                    {previousTitle}" link above and the "Back to Find care" button in the
                                    footer for navigation, so the X here was redundant only on this level. */}
                                {subcategoryPath.length === 0 && (
                                  <button 
                                      className="close-button" 
                                      onClick={handleClosePanel}
                                      aria-label="Close panel"
                                  >
                                      ×
                                  </button>
                                )}
                            </div>
                        </div>
                        <div id="panel-content" className="panel-content">
                            <div className="looking-for-label" role="heading" aria-level="4">
                                {overlayTitle}
                            </div>
                            {/* A11y fix: this container and its "items" (emergency entry, quick
                                links, more results, subcategory buttons) previously used
                                role="list"/role="listitem" on plain <div>/<button> elements —
                                but several "items" were actually buttons wrapped in an
                                intermediate, role-less <div> (e.g. .category-item-leaf,
                                .quick-links-section), which breaks the required direct
                                list->listitem ownership ARIA needs, so screen readers
                                (NVDA/JAWS/TalkBack/VoiceOver) didn't reliably announce this
                                as a real list. Replaced with genuine semantic <ul>/<li>
                                markup below (one <ul> per section) instead of ARIA role
                                emulation — see the matching list-style/margin/padding reset
                                in styles.css so the visual flex layout is unchanged. */}
                            <div className="category-list">
                                {/* Emergency entry for Where to get care and Types of care.
                                    Hidden entirely (button, link AND the 911 informational copy)
                                    when the plan's top-level featureFlags.emergencyCareBrowseBy
                                    flag is explicitly 'N' — plans that only want approved care
                                    categories shown in this side sheet (e.g. NY) opt out that way.
                                    A missing flag defaults to showing the entry. */}
                                {!isEmergencyEntryHidden && (selectedCategory === 'Where to get care' || selectedCategory === 'Types of care') && subcategoryPath.length === 0 && (
                                    <div className="emergency-entry">
                                        <ul className="emergency-list">
                                            {String(environmentId) !== '57' && (
                                                <li>
                                                    <button
                                                        className="category-item emergency-button"
                                                        onClick={handleEmergencyClick}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                handleEmergencyClick();
                                                            }
                                                        }}
                                                        type="button"
                                                        aria-label="Search for Emergency and Urgent Care"
                                                    >
                                                        <img src={starCircle} alt="" className="star-circle-icon" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
                                                        <span>Emergency and Urgent Care</span>
                                                        <img src={arrowRight} alt="" className="category-arrow" aria-hidden="true" style={{ marginLeft: '0.5rem' }} />
                                                    </button>
                                                </li>
                                            )}
                                            {String(environmentId) === '57' && (
                                                <li>
                                                    <button
                                                        className="category-item emergency-button"
                                                        onClick={handleEmergencyClick}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                handleEmergencyClick();
                                                            }
                                                        }}
                                                        type="button"
                                                        aria-label="Search for Behavioral health emergencies"
                                                    >
                                                        <img src={starCircle} alt="" className="star-circle-icon" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
                                                        <span>Behavioral health emergencies</span>
                                                        <img src={arrowRight} alt="" className="category-arrow" aria-hidden="true" style={{ marginLeft: '0.5rem' }} />
                                                    </button>
                                                </li>
                                            )}
                                        </ul>
                                        {String(environmentId) === '57' ? (
                                            <div className="emergency-description">
                                                <span><a className="emergency-911" href='https://suicidepreventionlifeline.org/' target='_blank' rel='noopener noreferrer' onClick={(e) => { e.preventDefault(); setPendingExternalUrl('https://suicidepreventionlifeline.org/'); setExternalLinkModalOpen(true); }}>988 Suicide & Crisis Lifeline:</a> Call or text <a className='emergency-911 emergency-911--medium' href="tel:988">988</a>. Trained counselors can help you 24 hours a day, 7 days a week. Or chat online with a counselor through their <a className='emergency-911' href='https://988lifeline.org/chat/' target='_blank' rel='noopener noreferrer' onClick={(e) => { e.preventDefault(); setPendingExternalUrl('https://988lifeline.org/chat/'); setExternalLinkModalOpen(true); }}>Lifeline Chat</a> service.</span><br /><br />
                                                <span>Call <a href="tel:911" className='emergency-911 emergency-911--medium'>911</a> or go to the nearest hospital if a life is in danger. You can use any hospital for emergency care. It doesn't have to be in our network. You don't need a referral or preapproval.
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="emergency-description">
                                                <span>Call <a className='emergency-911 emergency-911--medium' href="tel:911">911</a> or go to the nearest hospital if a life is in danger. You can use any hospital for emergency care. It doesn't have to be in our network. You don't need a referral or preapproval.</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* {isQuickLinksSection && isLabsPanel ? (
                                    <>
                                        <div className="quick-links-section">
                                            <div className="category-item-header" role="heading" aria-level="5">
                                                {quickLinksHeader}
                                            </div>
                                            {quickLinksSubtext && (
                                                <div
                                                    className="subcategory-description"
                                                    dangerouslySetInnerHTML={{ __html: quickLinksSubtext }}
                                                />
                                            )}
                                            <ul className="quick-links-list">
                                                {quickLinksItems.map((item, i) => {
                                                    // The backend's subCategory text for the LabCorp quick link has been
                                                    // seen coming through as something other than "Labcorp" (e.g. "Get
                                                    // tested"). Force the correct display label here, keyed off the
                                                    // externalLink's domain, so the button's on-screen text can never
                                                    // drift from "Labcorp" even if the backend copy is wrong/changes.
                                                    const isLabcorpLink = typeof item?.externalLink === 'string' && item.externalLink.includes('labcorp.com');
                                                    const label = isLabcorpLink ? 'Labcorp' : getCategoryLabel(item);
                                                    const itemIndex = subcategories.indexOf(item);
                                                    return (
                                                        <li key={`quicklink-${i}`}>
                                                            <button
                                                                className="category-item"
                                                                onClick={() => handleSubcategoryClick(item, itemIndex)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleSubcategoryClick(item, itemIndex);
                                                                    }
                                                                }}
                                                                type="button"
                                                                aria-label={`${label} (opens in new tab)`}
                                                            >
                                                                <span>{label}</span>
                                                                <img src={externalLink} alt="External link" className="category-arrow" aria-hidden="true" />
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                        {/* 30px gap from Quick links section above — see .more-results-section }
                                        <div className="more-results-section">
                                            <div className="category-item-header" role="heading" aria-level="5">
                                                {moreResultsHeader}
                                            </div>
                                            {moreResultsSubtext && (
                                                <div
                                                    className="subcategory-description"
                                                    dangerouslySetInnerHTML={{ __html: moreResultsSubtext }}
                                                />
                                            )}
                                            {/* Internal subcategory search (e.g. "Find more lab providers") — same
                                                handleSubcategoryClick flow as every other link-less category item,
                                                using the item's REAL label from the API response instead of the
                                                previous hardcoded "Labwork: more results" placeholder that didn't
                                                actually search or navigate anywhere. No external-link icon since
                                                this stays in-app rather than opening a new tab. }
                                            {moreResultsItem && (
                                                <ul className="more-results-list">
                                                    <li>
                                                        <button
                                                            className="category-item"
                                                            onClick={() => handleSubcategoryClick(moreResultsItem, subcategories.indexOf(moreResultsItem))}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    handleSubcategoryClick(moreResultsItem, subcategories.indexOf(moreResultsItem));
                                                                }
                                                            }}
                                                            type="button"
                                                            aria-label={`Search for ${getCategoryLabel(moreResultsItem)}`}
                                                        >
                                                            <span>{getCategoryLabel(moreResultsItem)}</span>
                                                            <img src={arrowRight} alt="" className="category-arrow" aria-hidden="true" />
                                                        </button>
                                                    </li>
                                                </ul>
                                            )}
                                        </div>
                                    </>
                                ) : isQuickLinksSection ? ( */}
                                { isQuickLinksSection ? (
                                    <>
                                        <div className="quick-links-section">
                                            <ul className="quick-links-list">
                                                {quickLinksItems.map((item, i) => {
                                                    // The backend's subCategory text for the LabCorp quick link has been
                                                    // seen coming through as something other than "Labcorp" (e.g. "Get
                                                    // tested"). Force the correct display label here, keyed off the
                                                    // externalLink's domain, so the button's on-screen text can never
                                                    // drift from "Labcorp" even if the backend copy is wrong/changes.
                                                    const isLabcorpLink = typeof item?.externalLink === 'string' && item.externalLink.includes('labcorp.com');
                                                    const isDentalLink = typeof item?.externalLink === 'string' && item.externalLink.includes('dental') || item.externalLink.includes('DentaQuest');
                                                    const label = isLabcorpLink ? 'Labcorp' : getCategoryLabel(item);
                                                    const itemIndex = subcategories.indexOf(item);
                                                    return (
                                                        <li key={`quicklink-${i}`}>
                                                            <button
                                                                className="category-item"
                                                                onClick={() => handleSubcategoryClick(item, itemIndex)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleSubcategoryClick(item, itemIndex);
                                                                    }
                                                                }}
                                                                type="button"
                                                                aria-label={`${label} (opens in new tab)`}
                                                            >
                                                                {renderLabelWithArrow(label, { src: externalLink, alt: 'External link', className: 'category-arrow', 'aria-hidden': 'true' })}
                                                            </button>
                                                            {(isLabsPanel || isDentalPanel) && (
                                                                <div
                                                                    className="subcategory-description"
                                                                    dangerouslySetInnerHTML={{ __html: quickLinksSubtext }}
                                                                />
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            {quickLinksSubtext && (!isLabsPanel && !isDentalPanel) && (
                                                <div
                                                    className="subcategory-description"
                                                    dangerouslySetInnerHTML={{ __html: quickLinksSubtext }}
                                                />
                                            )}
                                        </div>
                                        {/* 30px gap from Quick links section above — see .more-results-section */}
                                        <div className="more-results-section">
                                            {moreResultsItem && (
                                                <ul className="more-results-list">
                                                    <li>
                                                        <button
                                                            className="category-item"
                                                            onClick={() => handleSubcategoryClick(moreResultsItem, subcategories.indexOf(moreResultsItem))}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    handleSubcategoryClick(moreResultsItem, subcategories.indexOf(moreResultsItem));
                                                                }
                                                            }}
                                                            type="button"
                                                            aria-label={`Search for ${getCategoryLabel(moreResultsItem)}`}
                                                        >
                                                            {renderLabelWithArrow(getCategoryLabel(moreResultsItem), { src: arrowRight, alt: '', className: 'category-arrow', 'aria-hidden': 'true' })}
                                                        </button>
                                                    </li>
                                                </ul>
                                            )}
                                            {moreResultsSubtext && (
                                                <div
                                                    className="subcategory-description"
                                                    dangerouslySetInnerHTML={{ __html: moreResultsSubtext }}
                                                />
                                            )}
                                        </div>
                                    </>
                                ) : subcategories && subcategories.length > 0 ? (
                                    <ul className="subcategories-list">
                                    {subcategories.map((subcategory, index, arr) => {
                                        if (typeof subcategory === 'object' && subcategory.lookingForText && Array.isArray(subcategory.subCategories)) {
                                            return null;
                                        }

                                        // Consumed by the preceding string item (externalLink-only object)
                                        if (typeof subcategory === 'object' && hasExternalLink(subcategory) && !subcategory.lookingForText && !subcategory.subCategories && typeof arr[index - 1] === 'string') {
                                            return null;
                                        }

                                        // Check if this string item is followed by a subcategories or externalLink object
                                        if (typeof subcategory === 'string') {
                                            const next = arr[index + 1];
                                            if (next && typeof next === 'object' && next.lookingForText && Array.isArray(next.subCategories)) {
                                                // This string has subcategories - make it clickable
                                                return (
                                                    <li key={`supercategory-${index}`}>
                                                        <button
                                                            className="category-item"
                                                            onClick={() => {
                                                                handleSubcategoryClick(next, index + 1);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    handleSubcategoryClick(next, index + 1);
                                                                }
                                                            }}
                                                            type="button"
                                                            aria-label={`Show more options for ${subcategory}`}
                                                        >
                                                            {renderLabelWithArrow(subcategory, { src: arrowRight, alt: 'Show more', className: 'category-arrow', 'aria-hidden': 'true' })}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                            if (next && typeof next === 'object' && hasExternalLink(next) && !next.lookingForText && !next.subCategories) {
                                                // This string has an associated external link
                                                return (
                                                    <li key={`leafcategory-${index}`} className="category-item-leaf">
                                                        <button
                                                            className="category-item"
                                                            onClick={() => handleSubcategoryClick(next, index + 1)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    handleSubcategoryClick(next, index + 1);
                                                                }
                                                            }}
                                                            type="button"
                                                            aria-label={`${subcategory} (opens in new tab)`}
                                                        >
                                                            {renderLabelWithArrow(subcategory, { src: externalLink, alt: 'External link', className: 'category-arrow', 'aria-hidden': 'true' })}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                        }
                                        
                                        const label = getCategoryLabel(subcategory);
                                        const description = getCategoryDescription(subcategory);
                                        const isExternalLink = hasExternalLink(subcategory);
                                        
                                        return (
                                            <li key={`leafcategory-${index}`} className="category-item-leaf">
                                                <button
                                                    className="category-item"
                                                    onClick={() => handleSubcategoryClick(subcategory, index)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            handleSubcategoryClick(subcategory, index);
                                                        }
                                                    }}
                                                    type="button"
                                                    aria-label={`Search for ${label}`}
                                                >
                                                    {renderLabelWithArrow(label, {
                                                        src: isExternalLink ? externalLink : arrowRight,
                                                        alt: isExternalLink ? 'External link' : '',
                                                        className: 'category-arrow',
                                                        'aria-hidden': 'true'
                                                    })}
                                                </button>
                                                {description && (
                                                    <div 
                                                        className="subcategory-description" 
                                                        dangerouslySetInnerHTML={{ __html: description }}
                                                    />
                                                )}
                                            </li>
                                        );
                                    })}
                                    </ul>
                                ) : (
                                    <div 
                                        style={{ color: '#666', fontSize: '14px', padding: '16px', fontFamily: '"CVS Health Sans"' }}
                                        role="status"
                                    >
                                        No subcategories available for this category.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="panel-footer">
                            <button 
                                className="go-back-button"
                                onClick={subcategoryPath.length > 0 ? handleClosePanel : handleBack}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (subcategoryPath.length > 0) {
                                            handleClosePanel();
                                        } else {
                                            handleBack();
                                        }
                                    }
                                }}
                                type="button"
                                aria-label={subcategoryPath.length > 0 ? 'Back to Find care page' : 'Go back to Find care page'}
                            >
                                Back to Find care
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ExternalLinkModal
                isOpen={externalLinkModalOpen}
                onConfirm={handleExternalLinkConfirm}
                onCancel={handleExternalLinkCancel}
                url={pendingExternalUrl}
            />
        </>
    );
};

export default BrowseBy;
